import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { evenNights, shortName } from '../../../utils/tripLegs';
import type { LegDraft, WizardForm } from './types';

let legSeq = 0;

/**
 * ADR-0030: the wizard's destinations. Picking a prediction adds a leg and
 * clears the search; its coordinates land a moment later (the details call).
 */
export function useLegs(setForm: Dispatch<SetStateAction<WizardForm>>) {
  const { t } = useTranslation();

  const withName = (f: WizardForm, legs: LegDraft[]): WizardForm => ({
    ...f,
    legs,
    name: f.nameEdited || legs.length === 0
      ? (f.nameEdited ? f.name : '')
      : t('dashboard.newTripWizard.defaultName', { destination: legs.map(l => shortName(l.name)).join(' · ') }),
  });

  /** From the search: a new leg, or the coordinates of one just added. */
  function resolve(name: string, coords?: { lat: number; lng: number }) {
    setForm(f => {
      const existing = f.legs.find(l => l.name === name);
      if (existing) {
        if (!coords) return { ...f, destinationQuery: '' };
        return { ...f, legs: f.legs.map(l => l === existing ? { ...l, lat: coords.lat, lng: coords.lng } : l) };
      }
      const leg: LegDraft = { key: `leg${++legSeq}`, name, lat: coords?.lat, lng: coords?.lng, nights: 0 };
      return { ...withName(f, [...f.legs, leg]), destinationQuery: '' };
    });
  }

  /** Moving on with text typed but nothing picked: that text is a leg too. */
  function commitTyped() {
    setForm(f => {
      const name = f.destinationQuery.trim();
      if (!name || f.legs.some(l => l.name === name)) return { ...f, destinationQuery: '' };
      return { ...withName(f, [...f.legs, { key: `leg${++legSeq}`, name, nights: 0 }]), destinationQuery: '' };
    });
  }

  function remove(key: string) {
    setForm(f => withName(f, f.legs.filter(l => l.key !== key)));
  }

  function setNights(key: string, nights: number) {
    setForm(f => ({ ...f, legs: f.legs.map(l => l.key === key ? { ...l, nights: Math.max(0, nights) } : l) }));
  }

  /** Once the trip's length is known: an even split, unless the user's still adds up. */
  function fitTo(f: WizardForm, total: number): LegDraft[] {
    if (f.legs.reduce((a, l) => a + l.nights, 0) === total) return f.legs;
    const split = evenNights(f.legs.length, total);
    return f.legs.map((l, i) => ({ ...l, nights: split[i] }));
  }

  return { resolve, commitTyped, remove, setNights, fitTo };
}
